/* Copyright 2018 - 2021 Ellucian Company L.P. and its affiliates.
 * File: ApplicationsView.tsx
 * Type: Container component */

// #region Imports
import React from 'react';
import ReactDOM from 'react-dom';

// Core components
import Button from '@hedtech/powercampus-design-system/react/core/Button';
import Card, { CardContent } from '@hedtech/powercampus-design-system/react/core/Card';
import ConfirmationDialog from '@hedtech/powercampus-design-system/react/core/ConfirmationDialog';
import Divider from '@hedtech/powercampus-design-system/react/core/Divider';
import ExpansionPanel from '@hedtech/powercampus-design-system/react/core/ExpansionPanel';
import Grid from '@hedtech/powercampus-design-system/react/core/Grid';
import Layout, { withLayout } from '@hedtech/powercampus-design-system/react/components/Layout';
import List, { ListItem, ListItemText } from '@hedtech/powercampus-design-system/react/core/List';
import MessageStyled from '@hedtech/powercampus-design-system/react/core/MessageStyled';
import Text from '@hedtech/powercampus-design-system/react/core/Text';

// Internal Components
import SavedApplications from './SavedApplications';
import SubmittedApplications from './SubmittedApplications';

// Types
import { IApplicationsResources } from '../../../Types/Resources/Admissions/IApplicationsResources';
import { IForm } from '../../../Types/Form/IForm';
import { IJsonResult } from '@hedtech/powercampus-design-system/types/IJsonResult';
import { ILogData } from '@hedtech/powercampus-design-system/types/ILogData';
import { ISavedApplication } from '../../../Types/Applications/ISavedApplication';
import { ISubmittedApplication } from '../../../Types/Applications/ISubmittedApplication';

// Helpers
import Format from '@hedtech/powercampus-design-system/helpers/Format';
import LogData from '@hedtech/powercampus-design-system/helpers/LogData';
import Resolver from '@hedtech/powercampus-design-system/helpers/Resolver';

// Requests
import RequestsApplications from '../../../Requests/Admissions/Applications';

// Constants
import Constants from '@hedtech/powercampus-design-system/helpers/Constants';

// State Management
import LayoutActions from '@hedtech/powercampus-design-system/flux/actions/LayoutActions';
import LayoutStore from '@hedtech/powercampus-design-system/flux/stores/LayoutStore';
// #endregion Imports

// #region Types
interface IApplicationsViewState {
    applications: IForm[];
    componentError: boolean;
    openRemoveConfirmationDialog: boolean;
    resources?: IApplicationsResources;
    savedApplicationId?: number;
    savedApplicationName?: string;
    savedApplications?: ISavedApplication[];
    showApplicationStatus: boolean;
    showDecisionAdmit: boolean;
    submittedApplications?: ISubmittedApplication[]
}
// #endregion Types

// #region Component
class ApplicationsView extends React.Component<any, IApplicationsViewState> {
    private idModule: string;
    private idPage: string;

    public readonly state: Readonly<IApplicationsViewState>;

    public constructor(props: any) {
        super(props);

        // #region Initialize Variables and State
        this.idModule = 'Admissions';
        this.idPage = 'Applications';
        this.state = this.getInitialState();
        // #endregion Initialize Variables and State

        // #region Bind State Management Listeners
        LayoutStore.addLayoutReadyListener(this.onLayoutReady);
        // #endregion State Management Listeners
    }

    private getInitialState(): IApplicationsViewState {
        let resources: IApplicationsResources | undefined;
        if (this.state) {
            resources = this.state.resources;
        }
        return {
            applications: [],
            componentError: false,
            openRemoveConfirmationDialog: false,
            showApplicationStatus: false,
            showDecisionAdmit: false,
            resources: resources
        };
    }

    // #region Events

    private onRemoveSavedApplication = (event: any): void => {
        try {
            const savedApplicationId: string[] = event.target.id.split('|');
            this.setState({
                openRemoveConfirmationDialog: true,
                savedApplicationId: Number(savedApplicationId[1]),
                savedApplicationName: savedApplicationId[2]
            });
        }
        catch (e) {
            this.logError(LogData.fromException(this.onRemoveSavedApplication.name, e));
        }
    };

    private onClickRemoveButton = (): void => {
        try {
            const {
                savedApplicationId
            } = this.state;

            if (savedApplicationId) {
                LayoutActions.setLoading(true);
                RequestsApplications.postDeleteSavedApplication(savedApplicationId, this.resolveDeleteSavedApplication, this.logError);
            }
        }
        catch (e) {
            this.logError(LogData.fromException(this.onClickRemoveButton.name, e));
        }
    };

    private onCloseSavedApplicationModal = (): void => {
        try {
            this.setState({
                openRemoveConfirmationDialog: false,
                savedApplicationId: undefined
            });
        }
        catch (e) {
            this.logError(LogData.fromException(this.onCloseSavedApplicationModal.name, e));
        }
    };
    // #endregion Events

    // #region Error Functions
    private logError(logData: ILogData): void {
        LayoutActions.setLogData(logData);
    }

    private redirectError(code: number): void {
        LayoutActions.setRedirectCode(code);
    }

    // #endregion Error Functions

    // #region Resolvers
    private resolveDeleteSavedApplication = (json: string): void => {
        try {
            const result: IJsonResult | undefined = Resolver(json, this.resolveDeleteSavedApplication.name);
            if (result?.status) {
                if (result.data) {
                    this.setState({
                        openRemoveConfirmationDialog: false
                    });
                }
                RequestsApplications.getApplications(this.resolveGetApplications, this.logError);
            }
        }
        catch (e) {
            this.logError(LogData.fromException(this.resolveDeleteSavedApplication.name, e));
        }
    };

    private resolveGetApplications = (json: string): void => {
        try {
            const result: IJsonResult | undefined = Resolver(json, this.resolveGetApplications.name);
            let applications: IForm[] = [];
            let savedApplications: ISavedApplication[] | undefined = undefined;
            let logged: boolean = false;
            if (result?.status) {
                if (result.data) {
                    if (result.data.forms &&
                        result.data.forms.length > 0) {
                        applications = result.data.forms;
                    }

                    if (result.data.savedApplicationViewModel &&
                        result.data.savedApplicationViewModel.length > 0) {
                        savedApplications = result.data.savedApplicationViewModel
                    }

                    logged = result.data.logged;

                    this.setState({
                        applications: applications,
                        savedApplications: savedApplications
                    }, () => {
                        if (!logged) {
                            LayoutActions.hidePageLoader();
                        }
                    });
                }

                if (logged) {
                    RequestsApplications.getStatusSettings(this.resolveGetStatusSettings);
                }
            }
        }
        catch (e) {
            this.logError(LogData.fromException(this.resolveGetApplications.name, e));
        }
    };

    private resolveGetStatusSettings = (json: string): void => {
        try {
            const result: IJsonResult | undefined = Resolver(json, this.resolveGetStatusSettings.name);
            if (result?.status) {
                if (result.data) {
                    this.setState({
                        showApplicationStatus: result.data.showApplicationStatus,
                        showDecisionAdmit: result.data.showDecisionAdmit
                    });
                }
                RequestsApplications.getSubmittedApplications(this.resolveGetSubmittedApplications);
            }
        }
        catch (e) {
            this.logError(LogData.fromException(this.resolveGetStatusSettings.name, e));
        }
    };

    private resolveGetSubmittedApplications = (json: string): void => {
        try {
            const result: IJsonResult | undefined = Resolver(json, this.resolveGetSubmittedApplications.name);
            if (result?.status) {
                if (result.data
                    && result.data.submittedApplications
                    && result.data.submittedApplications.length > 0) {
                    this.setState({
                        submittedApplications: result.data.submittedApplications
                    }, LayoutActions.hidePageLoader);
                }
                else {
                    this.setState({
                        submittedApplications: undefined
                    }, LayoutActions.hidePageLoader);
                }
            }
        }
        catch (e) {
            this.logError(LogData.fromException(this.resolveGetSubmittedApplications.name, e));
        }
    };
    // #endregion Resolvers

    // #region State Management Events
    private onLayoutReady = (): void => {
        const ready: boolean = LayoutStore.getLayoutReady();
        const resources: IApplicationsResources | undefined = LayoutStore.getResources();

        if (resources) {
            document.title = resources.lblPageTitle;
        }

        if (ready) {
            this.setState({
                resources: resources
            });
        }
        else {
            this.setState({
                componentError: true
            }, () => this.logError(LogData.layoutNoReady(this.onLayoutReady.name)));
        }
    };
    // #endregion State Management Events

    // #region Lifecycle
    public componentDidMount(): void {
        try {
            RequestsApplications.getApplications(this.resolveGetApplications, this.logError);
        }
        catch (e) {
            this.logError(LogData.fromException(this.componentDidMount.name, e));
        }
    }

    public componentWillUnmount(): void {
        LayoutStore.removeLayoutReadyListener(this.onLayoutReady);
    }

    public componentDidCatch(error, info): void {
        this.setState({
            componentError: true
        }, () => {
            this.logError(LogData.fromComponentException(this.componentDidCatch.name, error, info));
            this.redirectError(500);
        });
    }
    // #endregion Lifecycle

    public render(): JSX.Element {
        const {
            applications,
            componentError,
            openRemoveConfirmationDialog,
            resources,
            savedApplicationName,
            savedApplications,
            showApplicationStatus,
            showDecisionAdmit,
            submittedApplications
        } = this.state;

        let applicationList: JSX.Element | undefined;

        const elements: JSX.Element[] = applications.map((app, i) => {

            const linkToApplicationForm = (): void => {
                window.location.assign(`${Constants.webUrl}/Admissions/ApplicationForm/${app.formSettingId}`);
            };

            return (
                <ListItem
                    id={`applications_${i}`}
                    key={`applications_${i}`}
                >
                    <ListItemText
                        id={`application_${i}`}
                        primary={(
                            <Button
                                TextProps={{
                                    size: 'h3'
                                }}
                                align="left"
                                id={`lnkApliccation_${i}`}
                                textVariantStyling="inline"
                                variant="text"
                                onClick={linkToApplicationForm}
                            >
                                {app.name}
                            </Button>
                        )}
                        secondary={(
                            <Text color="textPrimary">
                                {app.description}
                            </Text>
                        )}
                    />
                </ListItem>
            );
        })

        if (!componentError && resources) {
            if (applications && applications.length > 0) {
                if (savedApplications || submittedApplications) {
                    applicationList = (
                        <ExpansionPanel
                            id="epnlApplications"
                            header={(
                                <Text size="h2">
                                    {resources.lblApplications}
                                </Text>
                            )}
                        >
                            <Divider
                                noMarginBottom
                                noMarginTop
                            />
                            <List id="lstApplicationsList">
                                {elements}
                            </List>
                        </ExpansionPanel>
                    );
                }
                else {
                    applicationList = (
                        <Card>
                            <CardContent>
                                <Grid container spacing={3}>
                                    <Grid item xs>
                                        <Text size="h2">
                                            {resources.lblApplications}
                                        </Text>
                                        <Divider />
                                        <List id="applicationsList">
                                            {elements}
                                        </List>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    );
                }
            }
            else {
                applicationList = (
                    <MessageStyled
                        classMessage="noResults"
                        message={resources.lblNoResults}
                    />
                );
            }
        }

        let savedApplicationList: JSX.Element | undefined;
        if (resources && savedApplications) {
            savedApplicationList = (
                <SavedApplications
                    savedApplications={savedApplications}
                    resources={resources.savedApplicationsResources}
                    onRemove={this.onRemoveSavedApplication}
                />
            );
        }

        let submittedApplicationList: JSX.Element | undefined;
        if (resources && submittedApplications) {
            submittedApplicationList = (
                <SubmittedApplications
                    showApplicationStatus={showApplicationStatus}
                    showDecisionAdmit={showDecisionAdmit}
                    submittedApplications={submittedApplications}
                    resources={resources.submittedApplicationsResources}
                />
            );
        }

        let removeSavedApplicatioModal: JSX.Element | undefined;
        if (resources && openRemoveConfirmationDialog) {
            removeSavedApplicatioModal = (
                <ConfirmationDialog
                    contentText={Format.toString(resources.savedApplicationsResources.lblRemoveConfirmation, [savedApplicationName])}
                    open={openRemoveConfirmationDialog}
                    primaryActionOnClick={this.onClickRemoveButton}
                    primaryActionText={resources.savedApplicationsResources.lblAcceptConfirmation}
                    secondaryActionOnClick={this.onCloseSavedApplicationModal}
                    secondaryActionText={resources.savedApplicationsResources.lblCancelConfirmation}
                    title={resources.savedApplicationsResources.lblTitle}
                />
            );
        }

        return (
            <Layout
                idModule={this.idModule}
                idPage={this.idPage}
                showCart
                showNotifications
            >
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        {submittedApplicationList}
                    </Grid>
                    <Grid item xs={12}>
                        {savedApplicationList}
                    </Grid>
                    <Grid item xs={12}>
                        {applicationList}
                    </Grid>
                </Grid>
                {removeSavedApplicatioModal}
            </Layout>
        );
    }
}

const ApplicationsViewWithLayout = withLayout(ApplicationsView);
// #endregion Component

// RenderDOM: Component
ReactDOM.render(<ApplicationsViewWithLayout />, document.getElementById('root'));